/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { AccessType } from '@quintype/components';
import get from 'lodash/get';
import './user-details.m.css';
import { UserProfile } from './user-profile';
import { PurchasedStoriesBlock } from './purchase-stories';
import LoaderOverlay from '../../molecules/loader-overlay';
import { SignInMessage } from '../../atoms/sign-in-message';
import { NotFoundPage } from '../not-found';

const UserContent = ({
  enableAccesstype,
  currentScreen,
  isAccessTypeLoaded,
  member,
  updateMember,
  getSubscriptionForUser,
  myProfile,
  purchasedStories,
  localizedMonths,
  memberSubscriptions,
  profileConfig,
  config,
  isDarkModeEnabled
}) => {
  switch (currentScreen) {
    case 'Profile':
      return (
        <UserProfile
          member={member}
          updateMember={updateMember}
          myProfile={myProfile}
          profileConfig={profileConfig}
          isDarkModeEnabled={isDarkModeEnabled}
        />
      );
    case 'PurchasedStories':
      return (
        <PurchasedStoriesBlock
          purchasedStories={purchasedStories}
          localizedMonths={localizedMonths}
          config={config}
          isDarkModeEnabled={isDarkModeEnabled}
          isAccessTypeLoaded={isAccessTypeLoaded}
          enableAccesstype={enableAccesstype}
          memberSubscriptions={memberSubscriptions}
          getSubscriptionForUser={getSubscriptionForUser}
        />
      );
    default:
      return <NotFoundPage />;
  }
};

UserContent.propTypes = {
  isAccessTypeLoaded: PropTypes.bool,
  enableAccesstype: PropTypes.bool,
  getSubscriptionForUser: PropTypes.func,
  member: PropTypes.object,
  subscriptions: PropTypes.arrayOf(PropTypes.object),
  currentScreen: PropTypes.string,
  localizedSubscribe: PropTypes.string,
  localizedMonths: PropTypes.object,
  profileConfig: PropTypes.object,
  config: PropTypes.object,
  myProfile: PropTypes.object,
  purchasedStories: PropTypes.object,
  memberSubscriptions: PropTypes.object,
  isDarkModeEnabled: PropTypes.bool
};

class UserDetailsBase extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeIndex: 0,
      currentScreen: 'Profile',
      enableSubscriptionMenu: false
    };
    this.loadSubscriptionMenu = this.loadSubscriptionMenu.bind(this);
  }

  componentDidMount() {
    if (this.props?.pageType === 'purchased-details') {
      this.setState({ currentScreen: 'PurchasedStories' });
    }
  }

  loadSubscriptionMenu = () => {
    this.setState({ enableSubscriptionMenu: true });
  };

  handleClick = (currentScreen, index) => {
    this.setState({ activeIndex: index });
    this.setState({ currentScreen });
  };

  render() {
    const user = this.props.member;
    const config = this.props.config;
    const sketchesHost = get(config, ['sketches-host'], '');
    const { rows = [] } = get(this.props, ['config', 'pagebuilder-config', 'profilePage'], {});
    const isDarkModeEnabled = get(this.props, ['header', 'isDarkModeEnabled'], false);

    if (!this.props.memberLoaded) return <LoaderOverlay />;

    if (user && user['verification-status']) {
      const { email = '', 'phone-number': phone = '' } = user;
      const { accesstypeIntegration: PbAccessTypeConfig = {}, localization = {} } = get(
        this.props,
        ['config', 'pagebuilder-config', 'general'],
        {}
      );

      const isStaging = get(PbAccessTypeConfig, ['accesstypeMode']) === 'staging';
      const {
        accesstypeKey = '',
        bridgeKeeperIntegrationId = '',
        enableAccesstype = false,
        disableMetering = true,
        accessTypeProdHost = '',
        accessTypeStgHost = '',
        enablePayPerAsset = false
      } = PbAccessTypeConfig;

      const { enableLocalization = false, localizedElements = {} } = localization;
      const {
        buttonLabels = {},
        myProfile = {},
        mySubscriptions = {},
        purchasedStories = {},
        months = {}
      } = enableLocalization ? localizedElements : {};
      const { subscribe: localizedSubscribe } = buttonLabels;

      if (this.state.currentScreen === 'PurchasedStories' && (!enablePayPerAsset || !enableAccesstype)) {
        return <NotFoundPage />;
      }
      
      return (
        <div styleName="user-detail-wrapper ">
          <div styleName="linked-components">
            <AccessType
              enableAccesstype={enableAccesstype}
              isStaging={isStaging}
              accessTypeKey={accesstypeKey}
              accessTypeBkIntegrationId={bridgeKeeperIntegrationId}
              email={email}
              phone={phone}
              sketchesHost={sketchesHost}
              disableMetering={disableMetering}
              prodHost={accessTypeProdHost}
              stagingHost={accessTypeStgHost}
            >
              {({ getSubscriptionForUser }) => {
                return (
                  <UserContent
                    enableAccesstype={enableAccesstype}
                    member={user}
                    updateMember={this.props.updateMember}
                    currentScreen={this.state.currentScreen}
                    getSubscriptionForUser={getSubscriptionForUser}
                    isAccessTypeLoaded={!!global.AccessType} // TODO: AT should provide this flag
                    localizedSubscribe={localizedSubscribe}
                    myProfile={myProfile}
                    mySubscriptions={mySubscriptions}
                    purchasedStories={purchasedStories}
                    localizedMonths={months}
                    memberSubscriptions={this.props.memberSubscriptions}
                    profileConfig={rows?.[0]?.config || {}}
                    config={config}
                    isDarkModeEnabled={isDarkModeEnabled}
                  />
                );
              }}
            </AccessType>
          </div>
        </div>
      );
    } else return <SignInMessage />;
  }
}

const mapStateToProps = (state) => {
  return {
    config: get(state, ['qt', 'config']),
    member: get(state, ['member'], {}),
    header: get(state, ['header'], {}),
    memberSubscriptions: get(state, ['memberSubscriptions']),
    memberLoaded: get(state, ['memberLoaded'], false),
    pageType: get(state, ['qt', 'pageType'])
  };
};

const mapDispatchToProps = (dispatch) => ({
  updateMember: (member) => dispatch({ type: 'MEMBER_UPDATED', member })
});

UserDetailsBase.propTypes = {
  config: PropTypes.object,
  member: PropTypes.shape({
    email: PropTypes.string,
    'phone-number': PropTypes.string,
    'verification-status': PropTypes.string
  }),
  pageType: PropTypes.string
};

const UserDetails = connect(mapStateToProps, mapDispatchToProps)(UserDetailsBase);

export { UserDetails };
